"use client";

import {
  CalendarDays,
  Clock,
  Utensils,
  BedDouble,
  MapPin,
  Star,
  Globe,
} from "lucide-react";
import { useMemo } from "react";
import { quoteThemeCssVars } from "../quote-theme/quote-theme-css-vars";
import { iAccommodationLegDetails } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { Button } from "@/components/ui/button";
import { SimpleCollapse } from "@/components/common/simple-collapse/simple-collapse";
import { GoogleReviews } from "../../google-maps-helpers/google-places-reviews";
import { ImageGallery } from "@/components/common/image-gallery/image-gallery";
import { HtmlContentWithReadMore } from "@/components/common/html-content-with-read-more";
import { formatConsistentDate, daysBetweenDateStrings } from "@/utils/timezone-date-utils";
import { GlobalConfig } from "@/config";

export interface HotelItineraryInnerProps extends iAccommodationLegDetails {
  primaryColor?: string;
  day?: number;
}

export function HotelItineraryInner(props: HotelItineraryInnerProps) {
  const primaryColor = quoteThemeCssVars.primary;
  const lightColor = quoteThemeCssVars.primary10;

  const formatDate = (date: string | null) => {
    if (!date) return "";
    return formatConsistentDate(date, "EEE, d MMM yyyy");
  };

  const getDurationInNights = () => {
    if (props.rooms.length === 0) return 0;
    const firstRoom = props.rooms[0];
    if (!firstRoom.checkInDate || !firstRoom.checkOutDate) return 0;
    return daysBetweenDateStrings(firstRoom.checkInDate, firstRoom.checkOutDate);
  };

  const placeImages = useMemo(() => {
    return (props.place?.photos || [])
      .filter((item: { width: number; height: number }) => item.width > item.height)
      .map(
        (record: { photo_reference: string }) =>
          // `https://cloud.clickconnector.app/photos?photoReference=${record.photo_reference}`
             `${GlobalConfig.ENGINE_BASE_PATH}/location/photos?photoReference=${record.photo_reference}`
      )
      .slice(0, 6);
  }, [props.place?.photos]);

  const allImages = useMemo(() => {
    if ((props?.images?.length ?? 0) > 0) {
      return props.images;
    }
    return [...(placeImages || [])];
  }, [placeImages, props.images]);

  return (
    <div className="p-4 sm:p-6">
      {/* Hotel Header */}
      <div className="mb-4 sm:mb-6">
        <h3 className="text-lg sm:text-xl font-semibold">{props.accommodationName}</h3>
        {props.place && (
          <>
            <div className="text-sm text-muted-foreground mt-1">
              <div className="flex items-start gap-1 sm:gap-2">
                <Globe className="h-3 w-3 sm:h-4 sm:w-4 mt-1.5 sm:mt-1 flex-shrink-0" />
                <span className="break-words leading-relaxed">{props.place.formatted_address}</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 mt-1">
                <MapPin className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="truncate">{props.place.formatted_phone_number}</span>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 mt-1">
              <Star className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0 fill-yellow-400 text-yellow-400" />
              <span className="text-sm font-medium">{props.place.rating}</span>
              <span className="text-muted-foreground text-sm">
                ({props.place.user_ratings_total} ratings)
              </span>
            </div>
          </>
        )}

        {props.place && (
          <div className="flex gap-2 mt-2 mb-3 sm:mb-4">
            <Button variant="outline" size="sm" asChild className="gap-1 sm:gap-2 text-xs">
              <a
                href={`https://www.google.com/maps/search/?api=1&query_place_id=${props.place.place_id}&query=${encodeURIComponent(props.place.name)}`}
                target="_blank"
                referrerPolicy="no-referrer"
              >
                <MapPin className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Location</span>
                <span className="sm:hidden">Map</span>
              </a>
            </Button>
          </div>
        )}

        <div className="flex flex-wrap gap-2 sm:gap-3 mt-2">
          {props.rooms.map((room, index) => (
            <div key={room.id} className="flex items-center gap-2 sm:gap-3">
              {index > 0 && <span className="text-gray-400">•</span>}
              <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                <BedDouble
                  className="w-3 h-3 sm:w-4 sm:h-4"
                  style={{ color: primaryColor }}
                />
                <span className="hidden sm:inline">{room.roomType}</span>
                <span className="sm:hidden">{room.roomType.split(" ")[0]}</span>
              </span>
              <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                <Utensils
                  className="w-3 h-3 sm:w-4 sm:h-4"
                  style={{ color: primaryColor }}
                />
                <span className="hidden sm:inline">{room.boardBasis}</span>
                <span className="sm:hidden">{room.boardBasis.split(" ")[0]}</span>
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Stay Details */}
      {props.rooms.length > 0 && (
        <div className="mb-4 sm:mb-6">
          <div className="md:hidden">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4" style={{ color: primaryColor }} />
                <div>
                  <div className="font-medium text-sm">Check-in</div>
                  <div className="text-sm">{formatDate(props.rooms[0].checkInDate)}</div>
                  {props.rooms[0].checkInTime && (
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Clock className="w-3 h-3" />
                      {props.rooms[0].checkInTime}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4" style={{ color: primaryColor }} />
                <div>
                  <div className="font-medium text-sm">Check-out</div>
                  <div className="text-sm">{formatDate(props.rooms[0].checkOutDate)}</div>
                  {props.rooms[0].checkOutTime && (
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Clock className="w-3 h-3" />
                      {props.rooms[0].checkOutTime}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="hidden md:grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <CalendarDays className="w-5 h-5 mt-1" style={{ color: primaryColor }} />
              <div>
                <div className="font-medium text-base">Check-in</div>
                <div className="text-base">{formatDate(props.rooms[0].checkInDate)}</div>
                {props.rooms[0].checkInTime && (
                  <div className="flex items-center gap-1 text-sm text-gray-600">
                    <Clock className="w-4 h-4" />
                    {props.rooms[0].checkInTime}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CalendarDays className="w-5 h-5 mt-1" style={{ color: primaryColor }} />
              <div>
                <div className="font-medium text-base">Check-out</div>
                <div className="text-base">{formatDate(props.rooms[0].checkOutDate)}</div>
                {props.rooms[0].checkOutTime && (
                  <div className="flex items-center gap-1 text-sm text-gray-600">
                    <Clock className="w-4 h-4" />
                    {props.rooms[0].checkOutTime}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Duration Badge */}
      <div className="mb-4 sm:mb-6">
        <span
          className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-sm"
          style={{
            backgroundColor: lightColor,
            color: primaryColor,
          }}
        >
          {getDurationInNights()} nights
        </span>
      </div>

      {/* Overview */}
      {(props.overview || props.place?.editorial_summary?.overview) && (
        <div className="mb-4 sm:mb-6">
          <h4 className="font-medium mb-2 text-sm sm:text-base">Overview</h4>
          <HtmlContentWithReadMore
            html={props.overview || props.place?.editorial_summary?.overview || ""}
            maxWords={40}
          />
        </div>
      )}

      {props.place && (
        <SimpleCollapse title="Read Reviews" className="mt-2">
          <div className="mt-4 sm:mt-6">
            <GoogleReviews reviews={props.place.reviews} />
          </div>
        </SimpleCollapse>
      )}

      {props.latitude && props.longitude && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
          <a
            href={`https://maps.google.com/?q=${props.latitude},${props.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            <span className="hidden sm:inline">View on map</span>
            <span className="sm:hidden">Map</span>
          </a>
        </div>
      )}

      {allImages && allImages.length > 0 && (
        <div className="mt-2">
          <ImageGallery images={allImages} />
        </div>
      )}
    </div>
  );
}
